import { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Spinner, Alert, Button, Badge, ListGroup } from 'react-bootstrap';
import { client } from '../../lib/AmplifyClient';
import type { Schema } from '../../../amplify/data/resource';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';

type Issue   = Schema['Issue']['type'];
type Profile = Schema['Profile']['type'];

const implementPath = (id: string) => `/session/${encodeURIComponent(id)}/implement`;
const dcsQuizPath = (id: string) => `/session/${encodeURIComponent(id)}/dcs/quiz`;

function unwrap<T>(maybeWrapped: unknown): T | null {
  if (maybeWrapped && typeof maybeWrapped === 'object' && 'data' in (maybeWrapped as any)) {
    const { data } = maybeWrapped as { data: T | null };
    return data ?? null;
  }
  return (maybeWrapped as T) ?? null;
}
function unwrapList<T>(maybe: any): T[] {
  if (maybe && typeof maybe === 'object' && 'data' in maybe) {
    const { data } = maybe as { data: T[] | null };
    return data ?? [];
  }
  return (maybe as T[]) ?? [];
}

const safe = (s?: string | null) => (typeof s === 'string' && s.trim().length ? s.trim() : '');
const sameEmail = (a?: string | null, b?: string | null) => safe(a).toLowerCase() === safe(b).toLowerCase();

/** Safe JSON parser that accepts object or JSON string; returns null otherwise */
function parseJsonLoose(input: unknown): unknown | null {
  if (input == null) return null;
  if (typeof input === 'object') return input as any;
  if (typeof input === 'string') {
    const t = input.trim();
    if (!t) return null;
    try {
      return JSON.parse(t);
    } catch {
      return null;
    }
  }
  return null;
}

function asStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((x) => (x == null ? '' : String(x)))
    .map((x) => x.trim())
    .filter((x) => x.length > 0);
}

type PrettySolution = {
  id?: string;
  title?: string;
  summary?: string;
  steps: string[];
  benefits: string[];
  risksOrTradeoffs: string[];
  successMetric?: string;
};

/** Extracts a pretty, display-ready shape from varied bestSolutionJson structures */
function extractPrettySolution(raw: unknown): PrettySolution | null {
  const base: any = parseJsonLoose(raw);
  if (!base || typeof base !== 'object') return null;

  // Many workers nest details under "normalized"
  const n: any =
    (base as any).normalized && typeof (base as any).normalized === 'object'
      ? (base as any).normalized
      : base;

  // Collect with flexible key names
  const id: string | undefined =
    safe(n?.id) || safe((base as any)?.chosenId) || safe((base as any)?.chosenID) || undefined;

  const title: string | undefined = safe(n?.title) || safe((base as any)?.title) || undefined;

  const summary: string | undefined = safe(n?.summary) || safe((base as any)?.summary) || undefined;

  const steps = asStringArray(n?.steps || (base as any)?.steps);
  const benefits = asStringArray(n?.benefits || (base as any)?.benefits);
  const risksOrTradeoffs = asStringArray(
    n?.risksOrTradeoffs || n?.risks || n?.tradeoffs || (base as any)?.risksOrTradeoffs
  );

  const successMetric: string | undefined =
    safe(n?.successMetric) || safe((base as any)?.successMetric) || undefined;

  if (title || summary || steps.length || benefits.length || risksOrTradeoffs.length || successMetric) {
    return { id, title, summary, steps, benefits, risksOrTradeoffs, successMetric };
  }
  return null;
}

export default function ResolveComponent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [issue, setIssue] = useState<Issue | null>(null);
  const [initiatorProfile, setInitiatorProfile] = useState<Profile | null>(null);
  const [collaboratorProfile, setCollaboratorProfile] = useState<Profile | null>(null);
  const [myEmail, setMyEmail] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);

  // Live subscription refs
  const issueSubRef = useRef<null | (() => void)>(null);
  const profileSubRef = useRef<null | (() => void)>(null);

  /** --- Set ISSUE STATUS to 'resolved' immediately on load --- */
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        await client.models.Issue.update(({ id, status: 'resolved' } as unknown) as any);
        setIssue((prev) => (prev ? ({ ...prev, status: 'resolved' } as any) : prev));
      } catch {
        try {
          await client.models.Issue.update(({ id, issueStatus: 'resolved' } as unknown) as any);
          setIssue((prev) => (prev ? ({ ...prev, issueStatus: 'resolved' } as any) : prev));
        } catch {
          /* ignore */
        }
      }
    })();
  }, [id]);

  // Identify my email
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const user = await getCurrentUser().catch(() => null as any);
        const attrs = await fetchUserAttributes().catch(() => ({} as any));
        const email =
          safe(attrs?.email) ||
          safe((user as any)?.signInDetails?.loginId) ||
          safe((user as any)?.username) ||
          '';
        if (alive) setMyEmail(email);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Initial load of Issue + the two Profiles
  useEffect(() => {
    let alive = true;
    async function run() {
      if (!id) return;
      setLoading(true);
      setErr(null);
      try {
        const gotIssue = unwrap<Issue>(await client.models.Issue.get({ id }));
        if (!alive) return;
        if (!gotIssue) {
          setErr('Session not found.');
          setLoading(false);
          return;
        }
        setIssue(gotIssue);

        const [initL, collL] = await Promise.all([
          gotIssue.initiatorEmail
            ? client.models.Profile.list({ filter: { email: { eq: gotIssue.initiatorEmail } } })
            : Promise.resolve({ data: [] as Profile[] }),
          gotIssue.collaboratorEmail
            ? client.models.Profile.list({ filter: { email: { eq: gotIssue.collaboratorEmail } } })
            : Promise.resolve({ data: [] as Profile[] }),
        ]);
        if (!alive) return;
        setInitiatorProfile(unwrapList<Profile>(initL)[0] ?? null);
        setCollaboratorProfile(unwrapList<Profile>(collL)[0] ?? null);
      } catch (e: any) {
        if (!alive) return;
        setErr(e?.message ?? 'Failed to load session.');
      } finally {
        if (alive) setLoading(false);
      }
    }
    run();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, myEmail]);

  // Live subscribe to Issue updates
  useEffect(() => {
    if (!id) return;

    // cleanup previous
    issueSubRef.current?.();
    issueSubRef.current = null;

    const api: any = (client.models.Issue as any);
    const onUpdate = api?.onUpdate ?? api?.observe ?? null;
    if (!onUpdate) return;

    const maybeObservable = onUpdate({ filter: { id: { eq: id } } });

    const sub =
      typeof maybeObservable?.subscribe === 'function'
        ? maybeObservable.subscribe({
            next: (evt: any) => {
              const rec: Issue | null =
                (evt?.data as Issue) ??
                (evt?.value?.data?.onUpdateIssue as Issue) ??
                (evt?.element as Issue) ??
                null;
              if (rec && rec.id === id) setIssue(rec);
            },
            error: () => {},
          })
        : null;

    issueSubRef.current = () => {
      try {
        sub?.unsubscribe?.();
      } catch {}
    };

    return () => {
      issueSubRef.current?.();
      issueSubRef.current = null;
    };
  }, [id]);

  // Who am I?
  const amInitiator = useMemo(() => !!issue && sameEmail(issue?.initiatorEmail, myEmail), [issue, myEmail]);
  const amCollaborator = useMemo(() => !!issue && sameEmail(issue?.collaboratorEmail, myEmail), [issue, myEmail]);

  // My DCS from my Profile only
  const myProfileDcs = useMemo<string>(() => {
    if (amInitiator) return safe(initiatorProfile?.dcsMode);
    if (amCollaborator) return safe(collaboratorProfile?.dcsMode);
    return '';
  }, [amInitiator, amCollaborator, initiatorProfile?.dcsMode, collaboratorProfile?.dcsMode]);

  // Optional: subscribe to my Profile via emailLower to keep my dcsMode fresh
  const myEmailLower = safe(myEmail).toLowerCase();
  useEffect(() => {
    if (!myEmailLower) return;

    // cleanup previous
    profileSubRef.current?.();
    profileSubRef.current = null;

    const api: any = (client.models.Profile as any);
    const onUpdate = api?.onUpdate ?? api?.observe ?? null;
    if (!onUpdate) return;

    const maybeObservable = onUpdate({ filter: { emailLower: { eq: myEmailLower } } });
    const sub =
      typeof maybeObservable?.subscribe === 'function'
        ? maybeObservable.subscribe({
            next: (evt: any) => {
              const rec: Profile | null =
                (evt?.data as Profile) ??
                (evt?.value?.data?.onUpdateProfile as Profile) ??
                (evt?.element as Profile) ??
                null;

              if (!rec) return;
              if (amInitiator && sameEmail(rec.email, issue?.initiatorEmail)) {
                setInitiatorProfile((prev) => ({ ...(prev ?? ({} as any)), ...rec } as Profile));
              }
              if (amCollaborator && sameEmail(rec.email, issue?.collaboratorEmail)) {
                setCollaboratorProfile((prev) => ({ ...(prev ?? ({} as any)), ...rec } as Profile));
              }
            },
            error: () => {},
          })
        : null;

    profileSubRef.current = () => {
      try {
        sub?.unsubscribe?.();
      } catch {}
    };

    return () => {
      profileSubRef.current?.();
      profileSubRef.current = null;
    };
  }, [myEmailLower, amInitiator, amCollaborator, issue?.initiatorEmail, issue?.collaboratorEmail]);

  const agreedConflict = useMemo(() => {
    const fields = [
      (issue as any)?.agreedIssue,
      (issue as any)?.reframedIssue,
      safe((issue as any)?.precedencePrincipleHuman),
    ];
    return fields.find(Boolean) || '—';
  }, [issue]);

  /** Build a friendly, card-style display from bestSolutionJson (with fallbacks) */
  const prettySolution = useMemo<PrettySolution | null>(() => {
    if (!issue) return null;

    const jsonCandidates: unknown[] = [
      (issue as any)?.bestSolutionJson,
      (issue as any)?.bestSolutionJSON,
      (issue as any)?.bestSolutionObject,
      (issue as any)?.selectedSolutionJson,
      (issue as any)?.selectedSolutionJSON,
    ];

    const firstJsonish = jsonCandidates.find(
      (v) => v != null && (typeof v === 'object' || (typeof v === 'string' && v.trim().length > 0))
    );

    const extracted = extractPrettySolution(firstJsonish);
    if (extracted) return extracted;

    // Fallback: try legacy string fields
    const legacyFields = [
      (issue as any)?.bestSolution,
      (issue as any)?.bestSolutionStatement,
      (issue as any)?.selectedSolution,
      Array.isArray((issue as any)?.solutions) ? (issue as any).solutions?.[0] : '',
    ];
    const legacyRaw = safe(legacyFields.find(Boolean) as string);
    if (!legacyRaw) return null;

    const parsedLegacy = parseJsonLoose(legacyRaw);
    return extractPrettySolution(parsedLegacy) ?? {
      id: undefined,
      title: undefined,
      summary: legacyRaw,
      steps: [],
      benefits: [],
      risksOrTradeoffs: [],
      successMetric: undefined,
    };
  }, [issue]);

  const handleImplement = () => {
    if (!issue || !issue.id) return;
    if (!myProfileDcs) {
      navigate(dcsQuizPath(issue.id));
      return;
    }
    navigate(implementPath(issue.id));
  };

  if (loading) {
    return (
      <Container className="py-4 min-vh-100 d-flex align-items-center justify-content-center">
        <Card className="shadow-sm w-100" style={{ maxWidth: 800 }}>
          <Card.Body className="text-center">
            <Spinner animation="border" role="status" />
            <div className="mt-2">Loading your resolution…</div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (err || !issue) {
    return (
      <Container className="py-4 min-vh-100 d-flex align-items-center justify-content-center">
        <Card className="shadow-sm w-100" style={{ maxWidth: 800 }}>
          <Card.Body>
            <Alert variant="danger" className="mb-0">{err || 'Unable to load this session.'}</Alert>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-4 min-vh-100 d-flex align-items-center justify-content-center">
      <Card className="shadow-sm w-100" style={{ maxWidth: 800 }}>
        <Card.Header as="h5" className="d-flex flex-wrap gap-2 align-items-center">
          <span>Resolve</span>
          <Badge bg="success">Ready</Badge>
        </Card.Header>
        <Card.Body>
          <div className="mb-3">
            <div className="fw-semibold">Welcome 👋</div>
            <div className="d-flex flex-wrap gap-2 mt-2">
              <Badge bg="primary">
                Initiator:&nbsp;{safe((issue as any).initiatorFirstName) || safe((issue as any).initiatorEmail) || '—'}
              </Badge>
              <Badge bg="info" text="dark">
                Collaborator:&nbsp;{safe((issue as any).collaboratorFirstName) || safe((issue as any).collaboratorEmail) || '—'}
              </Badge>
            </div>
          </div>

          <ListGroup className="mb-3">
            <ListGroup.Item>
              <div className="fw-semibold">Agreed Conflict</div>
              <div className="mt-1">{agreedConflict}</div>
            </ListGroup.Item>

            <ListGroup.Item>
              <div className="fw-semibold mb-2">Best Solution</div>

              {prettySolution ? (
                <div>
                  {prettySolution.title && <div className="h6 mb-2">{prettySolution.title}</div>}

                  {prettySolution.summary && (
                    <p className="mb-2 text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                      {prettySolution.summary}
                    </p>
                  )}

                  {prettySolution.steps.length > 0 && (
                    <div className="mb-2">
                      <div className="fw-semibold">Steps</div>
                      <ol className="mb-0 mt-1">
                        {prettySolution.steps.map((step: string, idx: number) => (
                          <li key={idx} style={{ whiteSpace: 'pre-wrap' }}>
                            {step}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {prettySolution.benefits.length > 0 && (
                    <div className="mt-3">
                      <div className="fw-semibold">Benefits</div>
                      <ul className="mb-0 mt-1">
                        {prettySolution.benefits.map((b: string, idx: number) => (
                          <li key={idx} style={{ whiteSpace: 'pre-wrap' }}>
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {prettySolution.risksOrTradeoffs.length > 0 && (
                    <div className="mt-3">
                      <div className="fw-semibold">Risks / Tradeoffs</div>
                      <ul className="mb-0 mt-1">
                        {prettySolution.risksOrTradeoffs.map((r: string, idx: number) => (
                          <li key={idx} style={{ whiteSpace: 'pre-wrap' }}>
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {prettySolution.successMetric && (
                    <div className="mt-3">
                      <div className="fw-semibold">Success Metric</div>
                      <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                        {prettySolution.successMetric}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-muted">—</div>
              )}
            </ListGroup.Item>
          </ListGroup>

          <Alert variant="secondary" className="mb-3">
            Ready to implement? We’ll tailor guidance using each person’s conflict style once both profiles are set.
          </Alert>

          <div className="d-flex gap-2">
            <Button variant="success" onClick={handleImplement}>
              Implement Solutions
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}
